import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { AlertCircle, CheckCircle, Key01 } from '@untitledui/icons';
import { FeaturedIcon } from '@/components/_untitled/foundations/featured-icon/featured-icon';
import { authApi } from '@/api/authApi';
import type { KycStepProps } from '../../config/kycSteps.config';
import { ROUTES } from "@/config/routes.ts";
import { StepFooter } from '../StepFooter';
import { PasswordFields } from '../PasswordFields';
import { isPasswordValid } from '../../utils/passwordValidation';
import { prefixes, useTypedTranslation } from "@/locales";

export function ResetPasswordStep({ data, onBack }: KycStepProps) {
    const navigate = useNavigate();
    const [tResetPassword] = useTypedTranslation(prefixes('kyc.steps.resetPassword'));
    const [searchParams] = useSearchParams();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [tokenValidating, setTokenValidating] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);

    // Get token from URL or data
    const token = searchParams.get('token') || data.resetToken;

    // Validate token on mount
    useEffect(() => {
        const validateToken = async () => {
            if (!token) {
                setTokenValidating(false);
                setTokenValid(false);
                return;
            }

            try {
                const response = await authApi.validateResetToken(token);
                setTokenValid(response.valid);
            } catch {
                setTokenValid(false);
            } finally {
                setTokenValidating(false);
            }
        };

        validateToken();
    }, [token]);

    const canProceed = isPasswordValid(password) && password === confirmPassword && !success;

    const handleResetPassword = useCallback(async () => {
        if (!token) {
            setError(tResetPassword('invalidToken'));
            return;
        }

        try {
            setLoading(true);
            setError(null);

            await authApi.resetPassword(token, password);
            setSuccess(true);
        } catch (err: unknown) {
            if (err && typeof err === 'object' && 'response' in err) {
                const axiosError = err as { response?: { data?: { message?: string } } };
                const backendMessage = axiosError.response?.data?.message;
                setError(backendMessage || tResetPassword('error'));
            } else {
                setError(err instanceof Error ? err.message : tResetPassword('error'));
            }
        } finally {
            setLoading(false);
        }
    }, [token, password, tResetPassword]);

    const handleGoToLogin = useCallback(() => {
        navigate(ROUTES.HOME, { replace: true });
    }, [navigate]);

    // Show loading state while validating token
    if (tokenValidating) {
        return (
            <div className="flex flex-col items-center gap-6 py-6">
                <div className="animate-spin h-8 w-8 border-4 border-gray-300 border-t-brand-600 rounded-full"/>
                <p className="text-md text-tertiary text-center">
                    { tResetPassword('validatingToken') }
                </p>
            </div>
        );
    }

    // Show error state if no token or invalid token
    if (!token || !tokenValid) {
        return (
            <>
                <div className="flex flex-col items-center gap-6 py-6">
                    <FeaturedIcon icon={ AlertCircle } color="error" theme="light" size="xl"/>
                    <div className="flex flex-col items-center gap-2">
                        <h2 className="text-display-sm font-normal text-primary text-center">
                            { tResetPassword('invalidTokenTitle') }
                        </h2>
                        <p className="text-md text-tertiary text-center">
                            { tResetPassword('invalidTokenMessage') }
                        </p>
                    </div>
                </div>
                <StepFooter
                    primary={{
                        label: tResetPassword('backToLogin'),
                        onClick: handleGoToLogin
                    }}
                    back={{ show: true, onClick: onBack }}
                />
            </>
        );
    }

    // Show success state
    if (success) {
        return (
            <>
                <div className="flex flex-col items-center gap-6 py-6">
                    <FeaturedIcon icon={ CheckCircle } color="success" theme="light" size="xl"/>
                    <div className="flex flex-col items-center gap-2">
                        <h2 className="text-display-sm font-normal text-primary text-center">
                            { tResetPassword('successTitle') }
                        </h2>
                        <p className="text-md text-tertiary text-center whitespace-pre-line">
                            { tResetPassword('successMessage') }
                        </p>
                    </div>
                </div>
                <StepFooter
                    primary={{
                        label: tResetPassword('goToLogin'),
                        onClick: handleGoToLogin
                    }}
                    back={{ show: false }}
                />
            </>
        );
    }

    // Show password reset form
    return (
        <>
            <div className="flex flex-col items-center gap-6">
                <FeaturedIcon icon={ Key01 } color="brand" theme="light" size="xl"/>

                <div className="flex flex-col items-center gap-2 w-full">
                    <h2 className="text-display-sm font-normal text-primary text-center">
                        { tResetPassword('title') }
                    </h2>
                    <p className="text-md text-tertiary text-center whitespace-pre-line">
                        { tResetPassword('description') }
                    </p>
                </div>

                <div className="flex flex-col gap-6 w-full">
                    <PasswordFields
                        password={password}
                        confirmPassword={confirmPassword}
                        onPasswordChange={setPassword}
                        onConfirmPasswordChange={setConfirmPassword}
                        autoFocus
                    />

                    { error && (
                        <div className="flex items-start gap-2 rounded-lg bg-error-50 px-3 py-2.5">
                            <AlertCircle className="h-5 w-5 shrink-0 text-error-600 mt-0.5"/>
                            <p className="text-sm text-error-600">{ error }</p>
                        </div>
                    ) }
                </div>
            </div>

            <StepFooter
                primary={{
                    label: tResetPassword('submit'),
                    onClick: handleResetPassword,
                    disabled: !canProceed,
                    loading
                }}
                back={{ show: true, onClick: onBack }}
            />
        </>
    );
}
